import { Combobox, ComboboxProps, useComboboxFilter } from "@fluentui/react-components";
import React from "react";
import UserInfo from "../interface/UserInfo";

type BaseUserComboboxProps = {
    id: string,
    data: UserInfo[] | undefined,
    defaultValue: string,
    disabled: boolean,
    onChange: (value: string) => void;
};

export default function BaseUserCombobox({ id, data, defaultValue, disabled, onChange }: BaseUserComboboxProps) {
    const [query, setQuery] = React.useState<string>("");

    // Memoize userData to avoid unnecessary recalculations
    const userData = React.useMemo(() =>
        data?.map(user => ({
            children: `${user.employeeId} - ${user.displayName}`,
            value: user.userPrincipal.toString(),
            text: `${user.employeeId} - ${user.displayName}`,
        })) ?? [],
    [data]);

    // Filtered combobox items
    const comboboxItems = useComboboxFilter(query, userData, {
        noOptionsMessage: "No option match your search.",
        optionToText: user => user.text,
    });

    // Set query to displayName when defaultValue or data changes
    React.useEffect(() => {
        const defaultUser = data?.find(user => user.userPrincipal === defaultValue);
        setQuery(defaultUser ? `${defaultUser.employeeId} - ${defaultUser.displayName}` : "");
    }, [defaultValue, data]);

    // Handle option selection
    const onOptionSelect: ComboboxProps["onOptionSelect"] = (e, optionData) => {
        onChange(optionData.optionValue ?? "");
        setQuery(optionData.optionText ?? "");
    };

    // Handle input change
    const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(ev.target.value);
    };

    return (
        <Combobox
            id={id}
            value={query}
            onOptionSelect={onOptionSelect}
            onChange={handleInputChange}
            clearable
            disabled={disabled}
            style={{ width: "100%", zIndex: 999 }}
            inlinePopup
            positioning={{ fallbackPositions: ["below", "above"] }}
            placeholder="Select a user"
        >
            {comboboxItems}
        </Combobox>
    );
}